import {
  FontFamilyMedium,
  FontFamilyRegular,
  FontFamilySemibold,
  theme,
} from 'lib/theme';
import React from 'react';
import {
  LayoutChangeEvent,
  Platform,
  StyleProp,
  Text,
  TextProps,
  TextStyle,
} from 'react-native';

const TEXT_SIZE_XXXSMALL = 11;
const TEXT_SIZE_XXSMALL = 12;
export const TEXT_SIZE_XSMALL = 13;
export const TEXT_SIZE_SMALL = 14;
export const TEXT_SIZE_NORMAL = 15;
const TEXT_SIZE_LARGE = 16;
export const TEXT_SIZE_XLARGE = 20;

export enum TxtPreset {
  CardRowLabel = 'CardRowLabel',
  CardRowLabel2 = 'CardRowLabel2',
  CardRowValueInline = 'CardRowValueInline',
  CardRowValueInlineNoLabel = 'CardRowValueInlineNoLabel',
  CardRowValueUnder = 'CardRowValueUnder',
  CardRowValueUnderNoLabel = 'CardRowValueUnderNoLabel',
}

type Size =
  | 'xxxsmall'
  | 'xxsmall'
  | 'xsmall'
  | 'small'
  | 'normal'
  | 'large'
  | 'xlarge';

type Weight = 'regular' | 'medium' | 'bold';

const Presets: Record<
  TxtPreset,
  {
    normal: { size: Size; weight: Weight; color: string | undefined };
    small: { size: Size; weight: Weight; color: string | undefined };
  }
> = {
  [TxtPreset.CardRowLabel]: {
    normal: {
      size: 'normal',
      weight: 'regular',
      color: undefined,
    },
    small: {
      size: 'xsmall',
      weight: 'medium',
      color: undefined,
    },
  },
  [TxtPreset.CardRowLabel2]: {
    normal: {
      size: 'xsmall',
      weight: 'regular',
      color: undefined,
    },
    small: {
      size: 'xxsmall',
      weight: 'regular',
      color: undefined,
    },
  },
  [TxtPreset.CardRowValueInline]: {
    normal: {
      size: 'normal',
      weight: 'regular',
      color: theme.colors.fieldValueText,
    },
    small: {
      size: 'xsmall',
      weight: 'regular',
      color: theme.colors.fieldValueText,
    },
  },
  [TxtPreset.CardRowValueInlineNoLabel]: {
    normal: {
      size: 'normal',
      weight: 'regular',
      color: undefined,
    },
    small: {
      size: 'xsmall',
      weight: 'regular',
      color: undefined,
    },
  },
  [TxtPreset.CardRowValueUnder]: {
    normal: {
      size: 'xsmall',
      weight: 'regular',
      color: theme.colors.fieldValueText,
    },
    small: {
      size: 'xxsmall',
      weight: 'regular',
      color: theme.colors.fieldValueText,
    },
  },
  [TxtPreset.CardRowValueUnderNoLabel]: {
    normal: {
      size: 'normal',
      weight: 'regular',
      color: undefined,
    },
    small: {
      size: 'xsmall',
      weight: 'regular',
      color: undefined,
    },
  },
};

const WeightTranslation: Record<Weight, string> = {
  regular: FontFamilyRegular,
  medium: FontFamilyMedium,
  bold: FontFamilySemibold,
};

const SizeTranslation: Record<Size, number> = {
  normal: TEXT_SIZE_NORMAL,
  large: TEXT_SIZE_LARGE,
  xxxsmall: TEXT_SIZE_XXXSMALL,
  xxsmall: TEXT_SIZE_XXSMALL,
  xsmall: TEXT_SIZE_XSMALL,
  small: TEXT_SIZE_SMALL,
  xlarge: TEXT_SIZE_XLARGE,
};

export type TxtProps = {
  children?: React.ReactNode;
  size?: Size;
  weight?: Weight;
  style?: StyleProp<TextStyle>;
  numberOfLines?: number;
  onLayout?: (event: LayoutChangeEvent) => void;
  adjustsFontSizeToFit?: boolean;
  lineHeightNone?: boolean;
  selectable?: boolean;
  preset?: TxtPreset;
  presetSmall?: boolean;
  ellipsizeMode?: TextProps['ellipsizeMode'];
};

export const Txt: React.FC<TxtProps> = ({
  adjustsFontSizeToFit,
  children,
  numberOfLines,
  onLayout,
  size,
  style,
  weight,
  lineHeightNone,
  selectable = true,
  preset,
  presetSmall = false,
  ellipsizeMode,
}) => {
  let useDefaultSize: boolean = false;
  let fontSize: number;
  let fontFamily: string;
  const presetConfig = preset ? Presets[preset] : undefined;

  if (weight) {
    fontFamily = WeightTranslation[weight];
  } else if (presetConfig) {
    fontFamily = presetSmall
      ? WeightTranslation[presetConfig.small.weight]
      : WeightTranslation[presetConfig.normal.weight];
  } else {
    fontFamily = FontFamilyRegular;
  }

  if (size) {
    fontSize = SizeTranslation[size] ?? TEXT_SIZE_LARGE;
  } else if (presetConfig) {
    fontSize = presetSmall
      ? SizeTranslation[presetConfig.small.size]
      : SizeTranslation[presetConfig.normal.size];
    useDefaultSize = true;
  } else {
    fontSize = TEXT_SIZE_NORMAL;
    useDefaultSize = true;
  }

  const lineHeightMultiplier = 1.4;
  const lineHeight =
    lineHeightNone && Platform.OS !== 'ios'
      ? fontSize
      : Math.floor(fontSize * lineHeightMultiplier);

  return (
    <Text
      style={[
        {
          includeFontPadding: false,
          fontFamily,
        },
        useDefaultSize && !adjustsFontSizeToFit
          ? { fontSize, lineHeight }
          : undefined,
        style,
        !useDefaultSize && !adjustsFontSizeToFit
          ? { fontSize, lineHeight }
          : undefined,
      ]}
      selectable={selectable && Platform.OS === 'web'}
      numberOfLines={numberOfLines}
      onLayout={onLayout}
      adjustsFontSizeToFit={adjustsFontSizeToFit}
      allowFontScaling={!adjustsFontSizeToFit}
      minimumFontScale={0.3}
      ellipsizeMode={ellipsizeMode}
    >
      {children}
    </Text>
  );
};
